import { CheckCircleIcon, CircleIcon } from '@langchain/untitled-ui-icons';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';

import { formatDate } from '@/Pages/HubRepo/OptimizationRuns/utils';
import { Tooltip } from '@/design-system';
import { RuleSchema } from '@/types/schema';
import { cn } from '@/utils/tailwind';

import {
  CompositeScoreRule,
  isCompositeScoreRule,
} from '../utils/convertFeedbackFormulaToRule';
import { FeedbackKeyCell } from './FeedbackKeyCell';
import { ResourcesCell } from './ResourcesCell';
import { getEvaluatorActionColumn } from './getEvaluatorActionColumn';

export const columnHelper = createColumnHelper<RuleSchema>();

export type EvaluatorsColumnsDefProps = {
  organizationId: string | null;
  onOpenRuleLogs: (rule: RuleSchema) => void;
  areEvaluatorsAlignable: boolean;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getEvaluatorsColumnsDef: (
  props: EvaluatorsColumnsDefProps
) => ColumnDef<RuleSchema, any>[] = ({
  organizationId,
  onOpenRuleLogs,
  areEvaluatorsAlignable,
}) => [
  columnHelper.display({
    id: 'status',
    header: () => <div className="flex justify-center">Enabled</div>,
    enableResizing: false,
    enableSorting: false,
    cell: ({ row }) => {
      const isDisabled = !row.original.is_enabled;
      return !isDisabled ? (
        <div className="flex justify-center">
          <CheckCircleIcon className="h-4 w-4 text-green-600" />
        </div>
      ) : (
        <Tooltip description="This evaluator is disabled and will not run. Edit the evaluator prompt to enable it.">
          <div className="flex justify-center">
            <CircleIcon className="h-4 w-4" />
          </div>
        </Tooltip>
      );
    },
    size: 40,
  }),
  columnHelper.accessor('display_name', {
    header: () => <div className="pl-3">Name</div>,
    enableResizing: true,
    enableSorting: false,
    cell: ({ getValue }) => {
      const value = getValue();
      return value ? (
        <div className="overflow-x-auto pl-3 no-scrollbar">{value}</div>
      ) : (
        <i className="pl-3 text-tertiary">Unnamed evaluator</i>
      );
    },
  }),
  columnHelper.accessor(
    (row) => {
      // Check if this is a composite score first
      const compositeRow = row as CompositeScoreRule;
      if (isCompositeScoreRule(compositeRow)) {
        return 'Composite Score';
      }
      // Determine type based on evaluators vs code_evaluators
      if (row.evaluators && row.evaluators.length > 0) {
        if (row.group_by === 'thread_id') {
          return 'Thread';
        }
        return 'LLM-as-a-Judge';
      }
      if (row.code_evaluators && row.code_evaluators.length > 0) {
        return 'Code';
      }
      return 'Unknown';
    },
    {
      id: 'type',
      header: 'Type',
      enableResizing: true,
      enableSorting: false,
      cell: ({ getValue }) => {
        const value = getValue();
        return (
          <span
            className={cn(
              'inline-flex items-center rounded-full px-2 py-1 text-xs font-medium',
              {
                'border border-[var(--purple-200)] bg-[var(--purple-25)] text-[var(--purple-700)] dark:border-[var(--purple-50)]':
                  value === 'LLM-as-a-Judge',
                'border border-[var(--orange-200)] bg-[var(--orange-25)] text-[var(--orange-700)] dark:border-[var(--orange-50)]':
                  value === 'Composite Score',
                'border border-[var(--brand-200)] bg-[var(--brand-25)] text-[var(--brand-700)] dark:border-[var(--brand-50)]':
                  value === 'Thread',
                'border border-[var(--gray-200)] bg-[var(--gray-50)] text-[var(--gray-700)]':
                  value === 'Code',
              }
            )}
          >
            {value}
          </span>
        );
      },
    }
  ),
  columnHelper.display({
    id: 'feedback_keys',
    header: () => (
      <div className="line-clamp-1 min-w-0 whitespace-normal break-all">
        Feedback Key
      </div>
    ),
    enableResizing: true,
    enableSorting: false,
    cell: ({ row }) => {
      return <FeedbackKeyCell row={row.original} />;
    },
  }),
  columnHelper.display({
    id: 'resources',
    header: 'Resources',
    enableResizing: true,
    enableSorting: false,
    cell: ({ row }) => {
      return (
        <ResourcesCell
          row={row.original}
          organizationId={organizationId}
          onOpenRuleLogs={onOpenRuleLogs}
          areEvaluatorsAlignable={areEvaluatorsAlignable}
        />
      );
    },
    size: 200,
  }),
  columnHelper.display({
    id: 'updated_at',
    header: 'Modified At',
    enableResizing: true,
    enableSorting: false,
    cell: ({ row }) => {
      const value = row.original.updated_at;
      return value ? formatDate(value) : <i className="text-tertiary">—</i>;
    },
    size: 150,
  }),
  columnHelper.display({
    id: 'created_at',
    header: 'Created At',
    enableResizing: true,
    enableSorting: false,
    cell: ({ row }) => {
      const value = row.original.created_at;
      return value ? formatDate(value) : <i className="text-tertiary">—</i>;
    },
    size: 150,
  }),
  getEvaluatorActionColumn(),
];
